/**
 * WidgetRoot — Top-level component for the embeddable booking widget.
 *
 * Renders the V2 booking flow:
 *   - Collapsed: floating trigger button
 *   - Expanded: drawer panel with full booking flow
 *
 * Flow: Login → Verify → Date (1/5) → Time (2/5) → Players (3/5) → Review (4/5) → Pay (5/5) → Confirmation
 */

import { useState, useCallback, useMemo, useEffect } from 'preact/hooks'
import { ChevronUp, Minus, X, Sun, Moon } from './icons'
import { useBookingState, type BookingStepV2, STEP_LABELS, BOOKING_STEPS } from '../hooks/useBookingState'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { StepIndicator } from './StepIndicator'
import { WidgetFooter } from './WidgetFooter'
import { AnimatedTransition } from './AnimatedTransition'
import { LoginStep } from '../steps/LoginStep'
import { VerifyStep } from '../steps/VerifyStep'
import { GolferDetailsStep } from '../steps/GolferDetailsStep'
import { DateStep } from '../steps/DateStep'
import { TimeStep } from '../steps/TimeStep'
import { PlayersStep } from '../steps/PlayersStep'
import { SummaryStep } from '../steps/SummaryStep'
import { PaymentStep } from '../steps/PaymentStep'
import { ConfirmationStep } from '../steps/ConfirmationStep'
import type { BrandProfile } from '../types'
import { analytics } from '../analytics/events'
import { LoadingSpinner } from './LoadingSpinner'
import { Toast } from './Toast'
import { ProfileBar } from './ProfileBar'
import { ReservationBanner, saveReservation, clearReservation, getSavedReservation, type ReservationData } from './ReservationBanner'

type WidgetSkin = 'default' | 'dark'
const SKIN_ORDER: WidgetSkin[] = ['default', 'dark']

interface WidgetRootProps {
  brand: BrandProfile
  skipVerification?: boolean
  requirePhone?: boolean
  clubId?: string
  /** When true, the widget renders inline (always open, no floating button) */
  inline?: boolean
}

export function WidgetRoot({ brand, skipVerification, requirePhone, clubId, inline }: WidgetRootProps) {
  const is7MB = clubId === '7mb' || clubId === '7mile-beach'
  const [isOpen, setIsOpen] = useState(!!inline)
  const [isClosing, setIsClosing] = useState(false)
  const [activeSkin, setActiveSkin] = useState<WidgetSkin>(brand.skin || 'default')
  const [reservation, setReservation] = useState<ReservationData | null>(() => getSavedReservation())

  const cycleSkin = useCallback(() => {
    setActiveSkin(prev => {
      const idx = SKIN_ORDER.indexOf(prev)
      return SKIN_ORDER[(idx + 1) % SKIN_ORDER.length]
    })
  }, [])

  const widgetClass = activeSkin === 'default'
    ? 'bw2-widget'
    : `bw2-widget bw2-widget--${activeSkin}`

  const state = useBookingState({
    clubId,
    theme: brand.theme || 'dark',
    skipVerification,
    requirePhone,
    onBookingComplete: (bookingId) => {
      // Dispatch analytics event on host window
      try {
        window.parent.dispatchEvent(new CustomEvent('currents:booking-confirmed', {
          detail: { bookingId },
        }))
      } catch (_) { /* cross-origin safety */ }
    },
  })

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    setIsClosing(false)
    analytics.widgetOpened()
  }, [])

  const handleClose = useCallback(() => {
    setIsClosing(true)
    analytics.widgetClosed('user')
    setTimeout(() => {
      setIsOpen(false)
      setIsClosing(false)
    }, 200)
  }, [])

  // ─── Reservation Management ──────────────────────────────────────────
  const handleReservationExpired = useCallback(() => {
    setReservation(null)
    state.addToast('error', 'Your tee time hold has expired')
  }, [state.addToast])

  const handleClearReservation = useCallback(() => {
    clearReservation()
    setReservation(null)
  }, [])

  // Save reservation when a tee time is selected
  useEffect(() => {
    if (state.selectedTimeSlot && state.selectedDate) {
      const res = saveReservation(
        state.selectedTimeSlot.id,
        state.selectedTimeSlot.time,
        state.selectedTimeSlot.courseName || 'Course'
      )
      setReservation(res)
    }
  }, [state.selectedTimeSlot, state.selectedDate])

  // Focus trap + Escape key
  const panelRef = useFocusTrap<HTMLDivElement>({
    active: isOpen,
    onEscape: handleClose,
  })

  // Screen reader announcement for step changes
  const stepAnnouncement = useMemo(() => {
    const label = STEP_LABELS[state.currentStep]
    const bookingIdx = BOOKING_STEPS.indexOf(state.currentStep)
    if (bookingIdx >= 0) {
      return `Step ${bookingIdx + 1} of ${BOOKING_STEPS.length}: ${label}`
    }
    return label
  }, [state.currentStep])

  // Next button disabled logic
  const isNextDisabled = (): boolean => {
    switch (state.currentStep) {
      case 'date': return !state.selectedDate
      case 'time': return !state.selectedTimeSlot
      case 'players': return state.players.length === 0 || !state.players[0].firstName || !state.players[0].email
      case 'summary': return false
      case 'payment': return false
      default: return false
    }
  }

  const getNextLabel = (): string => {
    switch (state.currentStep) {
      case 'summary': return 'Confirm'
      case 'payment': return 'Pay Now'
      default: return 'Next'
    }
  }

  const showFooter = state.isBookingStep

  // Render current step
  const renderStep = () => {
    switch (state.currentStep) {
      case 'login':
        return (
          <LoginStep
            clubName={state.club.name}
            onLogin={state.login}
            brandImageUrl={brand.assets.logoUrl}
            brandImageDarkUrl={brand.assets.logoDarkUrl}
          />
        )
      case 'golfer-details':
        return (
          <GolferDetailsStep
            onSubmit={state.submitGolferDetails}
            initialEmail={state.auth.email || undefined}
            authMethod={state.auth.method || undefined}
            requirePhone={requirePhone}
          />
        )
      case 'verify':
        return (
          <VerifyStep
            clubName={state.club.name}
            onVerify={state.verifyProfile}
          />
        )
      case 'date':
        return (
          <DateStep
            courses={state.courses}
            selectedCourseId={state.selectedCourseId}
            onSelectCourse={state.selectCourse}
            availableDates={state.availableDates}
            selectedDate={state.selectedDate}
            onSelectDate={state.selectDate}
            timezone={state.club.timezone}
          />
        )
      case 'time':
        return (
          <TimeStep
            timeSlots={state.timeSlots}
            selectedTimeSlot={state.selectedTimeSlot}
            selectedDate={state.selectedDate}
            onSelectTimeSlot={state.selectTimeSlot}
            timezone={state.club.timezone}
          />
        )
      case 'players':
        return (
          <PlayersStep
            players={state.players}
            playerCount={state.playerCount}
            maxPlayers={state.selectedTimeSlot?.maxPlayers || 4}
            onUpdatePlayer={state.updatePlayer}
            onAddPlayer={state.addPlayer}
            onRemovePlayer={state.removePlayer}
            requirePhone={requirePhone}
            hideCart={is7MB}
          />
        )
      case 'summary':
        return (
          <SummaryStep
            clubName={state.club.name}
            selectedDate={state.selectedDate}
            selectedTimeSlot={state.selectedTimeSlot}
            players={state.players}
            priceBreakdown={state.priceBreakdown}
            timezone={state.club.timezone}
          />
        )
      case 'payment':
        return (
          <PaymentStep
            priceBreakdown={state.priceBreakdown}
          />
        )
      case 'confirmation':
        return (
          <ConfirmationStep
            bookingReference={state.confirmation?.bookingReference || 'N/A'}
            clubName={state.club.name}
            status={state.confirmation?.status || 'PENDING_VERIFICATION'}
            onReset={state.reset}
            brandLogoUrl={brand.assets.logoUrl}
          />
        )
      default:
        return null
    }
  }

  // Shared panel content
  const renderPanelContent = () => {
    // Determine if we should show profile bar
    const showProfileBar = state.verify.isVerified && state.isBookingStep && state.verify.profile
    // Only show reservation banner after a tee time has been selected (not just any booking step)
    const showReservation = reservation && state.selectedTimeSlot && state.isBookingStep && state.currentStep !== 'confirmation'

    return (
      <>
        {/* Screen reader: live region announces step changes */}
        <div className="bw2-sr-only" aria-live="polite" aria-atomic="true">
          {stepAnnouncement}
        </div>

        {/* Step Display (recessed LCD bar) */}
        <StepIndicator
          currentStep={state.currentStep}
          bookingStepIndex={state.bookingStepIndex}
          isBookingStep={state.isBookingStep}
        />

        {/* Profile Bar — shown after verification during booking steps */}
        {showProfileBar && state.verify.profile && (
          <ProfileBar
            firstName={state.verify.profile.firstName}
            lastName={state.verify.profile.lastName}
            email={state.auth.email || ''}
            homeClubName={state.verify.profile.homeClubName}
          />
        )}

        {/* Toast Notifications */}
        <Toast toasts={state.toasts} onDismiss={state.dismissToast} />

        {/* Content */}
        <div className="bw2-content" role="main">
          {state.loading.isLoading ? (
            <LoadingSpinner message={state.loading.message || undefined} />
          ) : (
            <AnimatedTransition stepKey={state.currentStep} direction={state.direction}>
              {renderStep()}
            </AnimatedTransition>
          )}
        </div>

        {/* Footer */}
        {showFooter && (
          <WidgetFooter
            canGoBack={state.canGoBack}
            canGoForward={state.canGoForward}
            onBack={state.goBack}
            onNext={state.currentStep === 'payment' ? state.confirmBooking : state.goNext}
            nextLabel={getNextLabel()}
            nextDisabled={isNextDisabled()}
          />
        )}

        {/* Reservation Banner — at the bottom, only after tee time selected */}
        {showReservation && (
          <ReservationBanner
            reservation={reservation}
            onExpired={handleReservationExpired}
            onClear={handleClearReservation}
          />
        )}
      </>
    )
  }

  // ─── Inline mode — always-open, no floating button ──────────────────────
  if (inline) {
    return (
      <div className={`${widgetClass} bw2-drawer-wrapper bw2-inline-wrapper`} data-theme={brand.theme || 'light'}>
        <div
          ref={panelRef}
          id="bw2-drawer-panel"
          className="bw2-panel bw2-panel--inline"
          role="region"
          aria-label={`Book a tee time at ${state.club.name}`}
        >
          {/* Title Bar — simplified for inline (no close/minimize) */}
          <div className="bw2-titlebar">
            <div className="bw2-titlebar__left">
              <div className="bw2-titlebar__icon">
                <img
                  src="https://www.currentsgolf.com/_next/image?url=%2FCurrents_Logo_Arctic_White.png&w=64&q=75"
                  alt="Currents"
                  className={activeSkin === 'default' ? 'bw2-logo--invert' : ''}
                />
              </div>
              <span className="bw2-titlebar__text">{state.club.name}</span>
            </div>
            <div className="bw2-titlebar__controls">
              <button
                className="bw2-a11y-toggle"
                onClick={cycleSkin}
                type="button"
                aria-label={`Switch to ${activeSkin === 'default' ? 'dark' : 'light'} mode`}
                title={`Mode: ${activeSkin === 'default' ? 'Light' : 'Dark'}`}
              >
                {activeSkin === 'default' ? <Moon size={14} /> : <Sun size={14} />}
              </button>
            </div>
          </div>

          {renderPanelContent()}
        </div>
      </div>
    )
  }

  // ─── Drawer mode (default) ──────────────────────────────────────────────
  return (
    <div className={`${widgetClass} bw2-drawer-wrapper`} data-theme={brand.theme || 'light'}>
      {!isOpen ? (
        /* Collapsed trigger bar */
        <button
          className="bw2-trigger"
          onClick={handleOpen}
          type="button"
          aria-expanded={false}
          aria-controls="bw2-drawer-panel"
        >
          <span className="bw2-trigger__icon" />
          <span className="bw2-trigger__label">{brand.button.text || 'Book Tee Time'}</span>
          <ChevronUp size={14} className="bw2-trigger__chevron" />
        </button>
      ) : (
        /* Expanded panel — dialog with focus trap */
        <div
          ref={panelRef}
          id="bw2-drawer-panel"
          className={`bw2-panel ${isClosing ? 'bw2-panel--closing' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={`Book a tee time at ${state.club.name}`}
          tabIndex={-1}
        >
          {/* Title Bar */}
          <div className="bw2-titlebar">
            <div className="bw2-titlebar__left">
              <div className="bw2-titlebar__icon">
                <img
                  src="https://www.currentsgolf.com/_next/image?url=%2FCurrents_Logo_Arctic_White.png&w=64&q=75"
                  alt="Currents"
                  className={activeSkin === 'default' ? 'bw2-logo--invert' : ''}
                />
              </div>
              <span className="bw2-titlebar__text">{state.club.name}</span>
            </div>
            <div className="bw2-titlebar__controls">
              <button
                className="bw2-a11y-toggle"
                onClick={cycleSkin}
                type="button"
                aria-label={`Switch to ${activeSkin === 'default' ? 'dark' : 'light'} mode`}
                title={`Mode: ${activeSkin === 'default' ? 'Light' : 'Dark'}`}
              >
                {activeSkin === 'default' ? <Moon size={14} /> : <Sun size={14} />}
              </button>
              <button
                className="bw2-titlebar__btn"
                onClick={handleClose}
                type="button"
                aria-label="Minimize"
              >
                <Minus size={14} />
              </button>
              <button
                className="bw2-titlebar__btn bw2-titlebar__btn--close"
                onClick={handleClose}
                type="button"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {renderPanelContent()}
        </div>
      )}
    </div>
  )
}
